import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import api from '../../services/api';
import type { User } from './userSlice';
import axios from 'axios';
import type { CartItem, OrderItemStatus, SelectedOptions } from '../../types/menu';
import { trackError, captureAppError, trackOrderConflict } from '../../utils/analytics';

interface InitiatePartialPaymentPayload {
  orderId: string;
  userId: string;
  amount: number;
  currency: string;
  paymentMethod: {
    type: string;
    provider: string;
    details?: Record<string, any>;
  };
  items: {
    orderItemId: string;
    amount: number;
  }[];
  metadata?: Record<string, any>;
}

interface AddOrderItemPayload {
  menuItemId: string;
  name: string;
  quantity: number;
  notes?: string;
  selectedOptions?: SelectedOptions;
}

export type OrderStatus = 'created' | 'ongoing' | 'preparing' | 'completed' | 'cancelled' | 'manually_closed' | 'manually_closed_by_customer';
export type PaymentStatus = 'pending' | 'paid' | 'failed' | 'refunded';

export interface OrderItem {
  id: string;
  menuItemId: string;
  name: string;
  price: number;
  quantity: number;
  notes?: string;
  options?: Record<string, any>;
  status?: OrderItemStatus;
  orderItemUser?: {
    user: User;
  };
  user?: User;
}

export interface Order {
  id: string;
  tableId: string;
  items: OrderItem[];
  status: OrderStatus;
  currency: string;
  paymentStatus: PaymentStatus;
  paymentMethod?: string;
  total: number;
  createdAt: string;
  updatedAt: string;
  restaurantId: string;
  restaurant?: {
    id: string;
    name: string;
    enablePayments: boolean;
    thirdPartyRecommendationsEnabled: boolean;
  };
  table?: {
    id?: string;
    number?: number;
    qrCode?: string;
  };
  splitDetails?: {
    totalSplits: number;
    amountPerSplit: number;
  };
}

interface OrderState {
  orders: Order[];
  currentOrder: Order | null;
  loading: boolean;
  error: string | null;
  wsConnected: boolean;
  items: CartItem[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

const initialState: OrderState = {
  orders: [],
  currentOrder: null,
  loading: false,
  error: null,
  wsConnected: false,
  items: [],
  status: 'idle',
};

export const fetchOrders = createAsyncThunk(
  'orders/fetchOrders',
  async () => {
    const response = await api.get<Order[]>('/orders');
    return response.data;
  }
);

export const getOrCreateCurrentOrder = createAsyncThunk(
  'orders/getOrCreateCurrentOrder',
  async (tableId: string) => {
    try {
      // First try to get the current order
      const response = await api.get<Order>(`/tables/${tableId}/current-order`);
      
      // Ensure we have all required fields and proper types
      const orderData: Order = {
        id: response.data.id,
        tableId: response.data.tableId,
        items: Array.isArray(response.data.items) ? response.data.items.map(item => {
          return {
            id: item.id,
            menuItemId: item.menuItemId,
            name: item.name,
            price: Number(item.price),
            quantity: Number(item.quantity),
            notes: item.notes || '',
            options: item.options || {},
            status: item.status,
            user: item.user || (item.orderItemUser && item.orderItemUser.user) || undefined
          };
        }) : [],
        status: response.data.status,
        currency: response.data.currency,
        paymentStatus: response.data.paymentStatus || 'pending',
        total: Number(response.data.total),
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
        restaurantId: response.data.restaurantId,
        restaurant: response.data.restaurant
      };
      
      return orderData;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // If no order exists, create a new one
        const deviceId = localStorage.getItem('deviceId');
        if (!deviceId) {
          throw new Error('No device ID found');
        }
        
        try {
          const createResponse = await api.post<Order>('/orders', { tableId, deviceId });
        
        // Fetch the newly created order to ensure we have all the data
        const fetchResponse = await api.get<Order>(`/orders/${createResponse.data.id}`);
        
        // Ensure we have all required fields and proper types
        const orderData: Order = {
          id: fetchResponse.data.id,
          tableId: fetchResponse.data.tableId,
          items: Array.isArray(fetchResponse.data.items) ? fetchResponse.data.items.map(item => {
            return {
              id: item.id,
              menuItemId: item.menuItemId,
              name: item.name,
              price: Number(item.price),
              quantity: Number(item.quantity),
              notes: item.notes || '',
              options: item.options || {},
              status: item.status,
              user: item.user || (item.orderItemUser && item.orderItemUser.user) || undefined
            };
          }) : [],
          status: fetchResponse.data.status,
          currency: fetchResponse.data.currency,
          paymentStatus: fetchResponse.data.paymentStatus || 'pending',
          total: Number(fetchResponse.data.total),
          createdAt: fetchResponse.data.createdAt,
          updatedAt: fetchResponse.data.updatedAt,
          restaurantId: fetchResponse.data.restaurantId,
          restaurant: fetchResponse.data.restaurant
        };
        
          return orderData;
        } catch (createError: any) {
          if (createError.response?.status === 409 && createError.response?.data?.code === 'ACTIVE_ORDER_EXISTS') {
            // Track this scenario for metrics and monitoring
            const existingOrderId = createError.response.data.existingOrderId;
            const existingOrderStatus = createError.response.data.existingOrderStatus;
            
            // Track both general error and specific order conflict
            trackError('ACTIVE_ORDER_EXISTS', 'Attempted to create order when active order already exists', 'order_creation');
            trackOrderConflict('ACTIVE_ORDER_EXISTS', {
              tableId,
              existingOrderId,
              existingOrderStatus,
              attemptSource: 'getOrCreateCurrentOrder'
            });
            captureAppError('Duplicate order creation attempt', {
              component: 'orderSlice',
              operation: 'getOrCreateCurrentOrder',
              severity: 'medium',
              extra: {
                tableId,
                existingOrderId,
                existingOrderStatus,
                attemptSource: 'getOrCreateCurrentOrder'
              }
            });
            
            // If there's already an active order, try to fetch it using the existing order ID
            if (existingOrderId) {
              console.log('Found existing active order, fetching it instead:', existingOrderId);
              const fetchResponse = await api.get<Order>(`/orders/${existingOrderId}`);
              
              // Return the existing order with proper formatting
              const orderData: Order = {
                id: fetchResponse.data.id,
                tableId: fetchResponse.data.tableId,
                items: Array.isArray(fetchResponse.data.items) ? fetchResponse.data.items.map(item => ({
                  id: item.id,
                  menuItemId: item.menuItemId,
                  name: item.name,
                  price: Number(item.price),
                  quantity: Number(item.quantity),
                  notes: item.notes || '',
                  options: item.options || {},
                  status: item.status,
                  user: item.user || (item.orderItemUser && item.orderItemUser.user) || undefined
                })) : [],
                status: fetchResponse.data.status,
                currency: fetchResponse.data.currency,
                paymentStatus: fetchResponse.data.paymentStatus || 'pending',
                total: Number(fetchResponse.data.total),
                createdAt: fetchResponse.data.createdAt,
                updatedAt: fetchResponse.data.updatedAt,
                restaurantId: fetchResponse.data.restaurantId,
                restaurant: fetchResponse.data.restaurant
              };
              
              return orderData;
            }
          }
          throw createError;
        }
      }
      throw error;
    }
  }
);

export const createOrder = createAsyncThunk(
  'orders/createOrder',
  async (orderData: { tableId: string; items: { menuItemId: string; quantity: number; notes?: string }[] }) => {
    const deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      throw new Error('No device ID found');
    }
    
    try {
      const response = await api.post<Order>('/orders', { ...orderData, deviceId });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 409 && error.response?.data?.code === 'ACTIVE_ORDER_EXISTS') {
        // Track this scenario for metrics and monitoring
        const existingOrderId = error.response.data.existingOrderId;
        const existingOrderStatus = error.response.data.existingOrderStatus;
        
        // Track both general error and specific order conflict
        trackError('ACTIVE_ORDER_EXISTS', 'Attempted to create order when active order already exists', 'order_creation');
        trackOrderConflict('ACTIVE_ORDER_EXISTS', {
          tableId: orderData.tableId,
          existingOrderId,
          existingOrderStatus,
          attemptSource: 'createOrder',
          requestedItems: orderData.items?.length || 0
        });
        captureAppError('Duplicate order creation attempt', {
          component: 'orderSlice',
          operation: 'createOrder',
          severity: 'medium',
          extra: {
            tableId: orderData.tableId,
            existingOrderId,
            existingOrderStatus,
            attemptSource: 'createOrder',
            requestedItems: orderData.items?.length || 0
          }
        });
        
        // If there's already an active order, fetch it and return it instead
        if (existingOrderId) {
          console.log('Cannot create new order - active order exists, fetching existing order:', existingOrderId);
          const fetchResponse = await api.get<Order>(`/orders/${existingOrderId}`);
          return fetchResponse.data;
        }
      }
      throw error;
    }
  }
);

export const updateOrderStatusAsync = createAsyncThunk(
  'orders/updateStatus',
  async ({ orderId, status }: { orderId: string; status: OrderStatus }) => {
    const response = await api.patch<Order>(`/orders/${orderId}/status`, { status });
    return response.data;
  }
);

export const processPayment = createAsyncThunk(
  'orders/processPayment',
  async ({ orderId, method, splitCount }: { orderId: string; method: string; splitCount?: number }) => {
    const response = await api.post<Order>(`/orders/${orderId}/payment`, { method, splitCount });
    return response.data;
  }
);

export const addOrderItemAsync = createAsyncThunk(
  'order/addItem',
  async ({ orderId, item }: { orderId: string; item: AddOrderItemPayload }, { rejectWithValue }) => {
    const deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      return rejectWithValue('No device ID found');
    }
    try {
        const response = await api.post(`/orders/${orderId}/items`, { ...item, deviceId });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            // Handle specific error messages for deleted items
            const errorMessage = error.response.data?.message || 'Error adding item to cart';
            return rejectWithValue(errorMessage);
          }
          return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const removeOrderItemAsync = createAsyncThunk(
  'order/removeItem',
  async ({ orderId, itemId, notes }: { orderId: string; itemId: string; notes?: string }) => {
    // Send notes as a query param if present
    const url = notes ? `/orders/${orderId}/items/${itemId}?notes=${encodeURIComponent(notes)}` : `/orders/${orderId}/items/${itemId}`;
    await api.delete(url);
    return { orderId, itemId, notes };
  }
);

interface UpdateOrderItemPayload {
  orderId: string;
  itemId: string; 
  updates: {
    quantity: number;
    price: number;
    notes?: string;
    options?: Record<string, any>;
  };
}

export const updateOrderItemAsync = createAsyncThunk(
  'order/updateItem',
  async ({ orderId, itemId, updates }: UpdateOrderItemPayload, { rejectWithValue }) => {
    try {
      const response = await api.patch<Order>(`/orders/${orderId}/items/${itemId}`, updates);
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const prepareMyItemsAsync = createAsyncThunk(
  'order/prepareMyItems',
  async (orderId: string, { rejectWithValue }) => {
    const deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      return rejectWithValue('Device ID not found');
    }
    try {
      const response = await api.post<Order>(`/orders/${orderId}/prepare-my-items`, { deviceId });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred');
    }
  }
);

export const initiatePartialPaymentAsync = createAsyncThunk(
  'orders/initiatePartialPayment',
  async (payload: InitiatePartialPaymentPayload, { rejectWithValue }) => {
    try {
      const response = await api.post('/payments/partial', payload);
      const { checkoutUrl } = response.data;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        return rejectWithValue('No checkout URL received from the server.');
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue('An unexpected error occurred while initiating payment.');
    }
  }
);

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setCurrentOrder: (state, action: PayloadAction<Order | null>) => {
      state.currentOrder = action.payload;
    },
    updateOrderStatus: (state, action: PayloadAction<{ orderId: string; status: OrderStatus }>) => {
      const order = state.orders.find(o => o.id === action.payload.orderId);
      if (order) {
        order.status = action.payload.status;
      }
      if (state.currentOrder?.id === action.payload.orderId) {
        state.currentOrder.status = action.payload.status;
      }
    },
    addOrderItem: (state, action: PayloadAction<OrderItem>) => {
        if (state.currentOrder) {
          const itemIndex = state.currentOrder.items.findIndex(item => item.id === action.payload.id);
          if (itemIndex > -1) {
            state.currentOrder.items[itemIndex] = action.payload;
          } else {
            state.currentOrder.items.push(action.payload);
          }
        }
    },
    removeOrderItem: (state, action: PayloadAction<{ itemId: string }>) => {
        if (state.currentOrder) {
            state.currentOrder.items = state.currentOrder.items.filter(
            item => item.id !== action.payload.itemId
            );
        }
    },
    updateItemQuantity: (
        state,
        action: PayloadAction<{ itemId: string; quantity: number }>
    ) => {
        if (state.currentOrder) {
            const item = state.currentOrder.items.find(
            item => item.id === action.payload.itemId
            );
            if (item) {
            item.quantity = action.payload.quantity;
            }
        }
    },
    updateItemNotes: (
        state,
        action: PayloadAction<{ itemId: string; notes: string }>
    ) => {
        if (state.currentOrder) {
            const item = state.currentOrder.items.find(
            item => item.id === action.payload.itemId
            );
            if (item) {
            item.notes = action.payload.notes;
            }
        }
    },
    clearCart: state => {
        if (state.currentOrder) {
            state.currentOrder.items = [];
        }
    },
    setWsConnected: (state, action: PayloadAction<boolean>) => {
      state.wsConnected = action.payload;
    },
    syncOrderUpdate: (state, action: PayloadAction<Order>) => {
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
            state.currentOrder = {
                ...state.currentOrder,
                ...action.payload,
                items: action.payload.items,
                restaurant: action.payload.restaurant
            };
        }
        const index = state.orders.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
            state.orders[index] = action.payload;
        }
    },
    updateOrderItemUserStatus: (state, action: PayloadAction<{ userId: string; status: OrderItemStatus }>) => {
       if (state.currentOrder) {
         state.currentOrder.items.forEach(item => {
           if (item.user?.id === action.payload.userId && item.status === 'added_to_cart') {
             item.status = action.payload.status;
           }
         });
       }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchOrders.pending, state => {
        state.loading = true;
      })
      .addCase(fetchOrders.fulfilled, (state, action: PayloadAction<Order[]>) => {
        state.loading = false;
        state.orders = action.payload;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch orders';
      })
      .addCase(getOrCreateCurrentOrder.pending, state => {
        state.loading = true;
      })
      .addCase(getOrCreateCurrentOrder.fulfilled, (state, action: PayloadAction<Order>) => {
        state.loading = false;
        state.currentOrder = action.payload;
      })
      .addCase(getOrCreateCurrentOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to get or create order';
      })
      .addCase(createOrder.fulfilled, (state, action: PayloadAction<Order>) => {
        state.orders.push(action.payload);
        state.currentOrder = action.payload;
      })
      .addCase(updateOrderStatusAsync.fulfilled, (state, action: PayloadAction<Order>) => {
        const index = state.orders.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      .addCase(processPayment.fulfilled, (state, action: PayloadAction<Order>) => {
        const index = state.orders.findIndex(o => o.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
        if (state.currentOrder && state.currentOrder.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      })
      .addCase(addOrderItemAsync.fulfilled, (state, action: PayloadAction<Order>) => {
          state.currentOrder = action.payload;
          const index = state.orders.findIndex(o => o.id === action.payload.id);
          if (index !== -1) {
              state.orders[index] = action.payload;
          }
      })
      .addCase(removeOrderItemAsync.fulfilled, (state, action: PayloadAction<any>) => {
        if (state.currentOrder && state.currentOrder.id === action.payload.orderId) {
            state.currentOrder.items = state.currentOrder.items.filter(
              item => item.id !== action.payload.itemId
            );
          }
      })
      .addCase(updateOrderItemAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateOrderItemAsync.fulfilled, (state, action: PayloadAction<Order>) => {
        state.status = 'succeeded';
        state.currentOrder = action.payload;
        const orderIndex = state.orders.findIndex(o => o.id === action.payload.id);
        if (orderIndex !== -1) {
          state.orders[orderIndex] = action.payload;
        }
      })
      .addCase(updateOrderItemAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as any)?.message || 'Failed to update item';
      })
      .addCase(prepareMyItemsAsync.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(prepareMyItemsAsync.fulfilled, (state, action: PayloadAction<Order>) => {
        state.status = 'succeeded';
        state.currentOrder = action.payload;
        const orderIndex = state.orders.findIndex(o => o.id === action.payload.id);
        if (orderIndex !== -1) {
          state.orders[orderIndex] = action.payload;
        } else {
          state.orders.push(action.payload);
        }
      })
      .addCase(prepareMyItemsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = (action.payload as any)?.message || 'Failed to prepare items';
      });
  },
});

export const {
  setCurrentOrder,
  updateOrderStatus,
  addOrderItem,
  removeOrderItem,
  updateItemQuantity,
  updateItemNotes,
  clearCart,
  setWsConnected,
  syncOrderUpdate,
  updateOrderItemUserStatus,
} = orderSlice.actions;

export default orderSlice.reducer; 